import React, { SetStateAction, useEffect, useState } from "react";
import {
  TouchableOpacity,
  TextInput,
  Platform,
  TextStyle,
  useWindowDimensions,
} from "react-native";
import { countries, _getCountry } from "./_lib/enhanced";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { CountryCodeProps } from "./types.js";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Modal from "../Modal";
import { Input, VDBText } from "..";
import FormContainer from "../Container";
import CountryFlag from "../CountryFlag";
import RadioButton from "../RadioButton";
import { DefaultTheme } from "styled-components/native";
import { css } from "styled-components/native";
interface CountryData {
  name: string;
  dial_code: string;
  code: string;
  flag: string;
}

const CountryCodeDropdownPicker = ({
  selected,
  setSelected,
  isGuest,
  disabled = false,
  setCountryDetails = () => {},
  phone,
  setPhone,
  phoneStyles = {},
  dropdownStyles = {},
  pickerIsOpen,
  fromBulkList = false,
  defaultValue = "",
  contentSized = false,
}: CountryCodeProps) => {
  const [_selected, _setSelected] = React.useState(false);
  const [_search, _setSearch] = React.useState("");
  const [_countries, _setCountries] = React.useState(countries);
  const theme = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const useBottomDrawer = !theme.isLargeScreen;
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(
    null
  );
  const userLoggedIn =
    (!!userInfo.user.country || !!userInfo.user.phone_number_country_code) &&
    !isGuest &&
    !fromBulkList;
  const pickerLocked = userLoggedIn || disabled;

  useEffect(() => {
    const fetchCountryData = async () => {
      let countryData;
      if (userInfo.user.country && !fromBulkList) {
        countryData = _getCountry(userInfo.user.country.toLowerCase());
      }
      if (
        !countryData &&
        userInfo.user.phone_number_country_code &&
        !fromBulkList
      ) {
        countryData = await _getCountry(
          "+" + userInfo.user.phone_number_country_code
        );
      }
      if (!countryData && !userLoggedIn && !fromBulkList) {
        countryData = _getCountry("US");
      }
      if (selected) {
        countryData = _getCountry("+" + selected);
      }
      if (fromBulkList) {
        countryData = selected
          ? _getCountry("+" + selected)
          : defaultValue
          ? _getCountry("+" + defaultValue)
          : null;
      }
      if (countryData) {
        setSelectedCountry(countryData as CountryData);
      } else if (fromBulkList && !selected && !defaultValue) {
        setSelectedCountry(null);
      }
    };
    fetchCountryData();
  }, [
    userInfo.user.country,
    userInfo.user.phone_number_country_code,
    selected,
    defaultValue,
    fromBulkList,
  ]);

  const slideDown = () => {
    _setSelected(true);
    pickerIsOpen && pickerIsOpen(true);
  };

  const slideUp = () => {
    _setSelected(false);
    _setSearch("");
    pickerIsOpen && pickerIsOpen(false);
  };

  const _searchCountry = (text: string) => {
    _setSearch(text);
    const query = text.toLowerCase().trim();
    const dialQuery = query.replace(/[\s+]/g, "");
    const c = countries.filter((item: CountryData) => {
      const nameMatch = item.name.toLowerCase().includes(query);
      const dialCode = item.dial_code.replace(/[\s+]/g, "");
      const dialMatch = dialQuery.length > 0 && dialCode.startsWith(dialQuery);
      return nameMatch || dialMatch;
    });
    _setCountries(c);
  };

  const RenderBtn = () => {
    if (!_selected) {
      return (
        <InputContainer contentSized={contentSized}>
          <TouchableOpacity
            style={
              contentSized
                ? { flexDirection: "row", alignSelf: "flex-start", height: 56 }
                : { flexDirection: "row" }
            }
            disabled={pickerLocked}
            onPress={() => {
              _setCountries(countries);
              slideDown();
            }}
          >
            <SelectedCountryContainer
              disabled={pickerLocked}
              fromBulkList={fromBulkList}
              contentSized={contentSized}
            >
              <CountryCodeTitle>
                <VDBText
                  type={typography["Body-R"]}
                  color={userLoggedIn ? theme["core-04"] : theme["core-06"]}
                  textAlign="center"
                  numberOfLines={contentSized ? 1 : undefined}
                >
                  {t("contactUs.input.countryCode.placeholder.text")}
                </VDBText>
              </CountryCodeTitle>
              <CountryValueContainer contentSized={contentSized}>
                {selectedCountry && (
                  <FlagSlot>
                    <CountryFlag isoCode={selectedCountry?.code} size={14} />
                  </FlagSlot>
                )}
                <VDBText
                  type={typography["Body-M"]}
                  color={pickerLocked ? theme["core-04"] : theme["core-05"]}
                  numberOfLines={1}
                >
                  {selectedCountry?.dial_code}
                </VDBText>
              </CountryValueContainer>
            </SelectedCountryContainer>
          </TouchableOpacity>
          {phone !== undefined && setPhone !== undefined && (
            <TextInput
              style={[
                {
                  marginLeft: 5,
                  paddingVertical: 5,
                  paddingLeft: 15,
                  flex: 1,
                  borderWidth: 1,
                  borderRadius: 8,
                  borderColor: "#dddddd",
                },
                phoneStyles as TextStyle,
              ]}
              placeholder={"Enter Mobile Number"}
              keyboardType={"phone-pad"}
              placeholderTextColor={"#dddddd"}
              onChangeText={setPhone}
              value={phone}
            />
          )}
        </InputContainer>
      );
    } else {
      renderTextInput();
    }
  };

  const renderTextInput = () => {
    const searchLabel = t(
      "contactUs.input.countrySearch.placeholder.text",
      "Search for Country"
    );
    return (
      <Input
        inputLabel={searchLabel}
        placeholder={searchLabel}
        value={_search}
        onChangeText={_searchCountry}
        rightIcon={"main-search"}
        autoCorrect={false}
        autoComplete="off"
      />
    );
  };

  const handleCountryPress = (item: CountryData) => {
    setSelected(item.dial_code.replace("+", ""));
    setCountryDetails?.(item as unknown as SetStateAction<undefined>);
    slideUp();
  };

  const orderedCountries = React.useMemo(() => {
    if (!selectedCountry) return _countries;
    const selectedItem = _countries.find(
      (item) => item.code === selectedCountry.code
    );
    if (!selectedItem) return _countries;
    return [
      selectedItem,
      ..._countries.filter((item) => item.code !== selectedItem.code),
    ];
  }, [_countries, selectedCountry]);

  const renderCountryItem = ({ item }: { item: CountryData }) => {
    const isSelected = selectedCountry?.code === item.code;
    const dialCode = item.dial_code.replace(/^\+\s*/, "+ ");
    return (
      <CountryContainer
        key={item.code}
        onPress={() => handleCountryPress(item)}
      >
        <RadioButton
          checked={isSelected}
          onPress={() => handleCountryPress(item)}
          hideLabel
          margin="0"
        />
        <FlagSlot>
          <CountryFlag isoCode={item?.code} size={14} />
        </FlagSlot>
        <VDBText
          type={isSelected ? typography["Body-M"] : typography["Body-R"]}
          color={isSelected ? theme["core-05"] : theme["core-06"]}
        >
          {`${dialCode} ${item.name}`}
        </VDBText>
      </CountryContainer>
    );
  };

  const sheetMaxHeight = Math.round(windowHeight * 0.9);
  const tabletSheetChrome = 140;
  const bottomDrawerListHeight = theme.isTablet
    ? sheetMaxHeight - tabletSheetChrome
    : Math.round(windowHeight * 0.62);

  const countryList = (
    <>
      {renderTextInput()}
      {_selected && (
        <FlatListContainer
          data={orderedCountries}
          style={[
            dropdownStyles,
            useBottomDrawer && {
              height: bottomDrawerListHeight,
              borderWidth: 0,
            },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          renderItem={renderCountryItem}
          keyExtractor={(item: CountryData) => item.code}
          ListEmptyComponent={
            <EmptyResultView>
              <VDBText
                type={typography["Body-R"]}
                color={"core-05"}
                textAlign={"center"}
              >
                No Result Found
              </VDBText>
            </EmptyResultView>
          }
        />
      )}
    </>
  );

  return (
    <>
      <Container>{RenderBtn()}</Container>
      <Modal
        headerTitle={t("contactUs.input.countryCode.placeholder.text")}
        centerTitle
        type={useBottomDrawer ? "sheet" : "popUp"}
        isOpen={_selected}
        onClose={() => slideUp()}
        paddingLeft={8}
        paddingRight={8}
        paddingBottom={0}
        webHeight={"80vh"}
        paddingTop={16}
        skipKeyboardAware={
          useBottomDrawer || theme.isLargeScreen || Platform.OS === "web"
        }
        showScrollbar={false}
      >
        {theme.isTablet ? (
          <FormContainer>{countryList}</FormContainer>
        ) : (
          countryList
        )}
      </Modal>
    </>
  );
};

export default CountryCodeDropdownPicker;

const Container = styled.View`
  width: 100%;
`;

const EmptyResultView = styled.View`
  padding: ${SpaceType.Double}px;
`;

const InputContainer = styled.View<{ contentSized?: boolean }>`
  flex-direction: row;
  align-items: center;
  ${({ contentSized }: { contentSized?: boolean }) =>
    contentSized &&
    css`
      align-self: flex-start;
      height: 56px;
    `}
`;

const CountryContainer = styled.TouchableOpacity`
  flex-direction: row;
  padding-horizontal: ${SpaceType.Double}px;
  padding-vertical: ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-bottom-color: #dedede;
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const FlagSlot = styled.View`
  width: 22px;
  height: 14px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const FlatListContainer = styled.FlatList`
  border-width: 1px;
  border-color: #dddddd;
  border-radius: ${SpaceType.Basic}px;
  background-color: white;
  margin-top: 10px;
`;

const CountryCodeTitle = styled.View`
  width: 100%;
  align-items: center;
`;

const SelectedCountryContainer = styled.View<{
  disabled: boolean;
  fromBulkList?: boolean;
  contentSized?: boolean;
}>`
  padding-left: 10px;
  padding-right: 10px;
  align-items: flex-start;
  justify-content: center;
  border-width: 1px;
  border-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-03"] : theme["core-04"])};
  border-radius: ${SpaceType.Basic}px;
  background-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-02"] : theme["core-01"])};
  height: 56px;
  min-height: 56px;
  max-height: 56px;
  ${({
    contentSized,
    fromBulkList,
  }: {
    contentSized?: boolean;
    fromBulkList?: boolean;
  }) =>
    contentSized
      ? css`
          width: auto;
          align-self: flex-start;
          overflow: hidden;
          position: relative;
          justify-content: flex-start;
          padding-top: 6px;
        `
      : css`
          min-width: ${fromBulkList ? "100%" : "20%"};
        `}
`;

const CountryValueContainer = styled.View<{ contentSized?: boolean }>`
  flex-direction: row;
  align-items: center;
  gap: 4px;
  ${({ contentSized }: { contentSized?: boolean }) =>
    contentSized
      ? css`
          position: absolute;
          left: 10px;
          bottom: 8px;
          padding-top: 0;
        `
      : css`
          padding-top: 4px;
          max-width: 100%;
        `}
`;
